import React, { useState, useEffect } from "react";
import { Plus, Save, Shield } from "lucide-react";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import ActionButtons, {
  PrimaryButton,
  SecondaryButton,
} from "../common/ActionButtons";
import {
  userServiceAccessApi,
  facilitiesApi,
  platformUsersApi,
} from "../../utils/api";
import { SERVICE_TYPES, SERVICE_ACCESS_ROLES } from "../../constants";

const UserServiceAccessManagement = () => {
  const [accessRecords, setAccessRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    user_id: "",
    facility_id: "",
    service_type: "rag",
    access_role: "user",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [accessData, usersData, facilitiesData] = await Promise.all([
        userServiceAccessApi.getAll(),
        platformUsersApi.getAll(),
        facilitiesApi.getAll(),
      ]);
      setAccessRecords(accessData);
      setUsers(usersData);
      setFacilities(facilitiesData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  const getField = (obj, ...keys) => {
    for (const key of keys) {
      if (obj[key] !== undefined) return obj[key];
    }
    return undefined;
  };

  const getUserName = (userId) => {
    const user = users.find((u) => (u.id || u.ID) === userId);
    if (!user) return userId || "-";
    const first = user.first_name || user.firstName || "";
    const last = user.last_name || user.lastName || "";
    return `${first} ${last}`.trim() || user.email || userId;
  };

  const getUserEmail = (userId) => {
    const user = users.find((u) => (u.id || u.ID) === userId);
    return user ? user.email || user.Email || "" : "";
  };

  const getFacilityName = (facilityId) => {
    const f = facilities.find(
      (fac) => (fac.facility_id || fac.facilityId || fac.id) === facilityId,
    );
    return f
      ? f.facility_name || f.facilityName || f.name || facilityId
      : facilityId || "-";
  };

  const getServiceLabel = (type) => {
    const s = SERVICE_TYPES.find((t) => t.value === type);
    return s ? s.label : type;
  };

  const getServiceColor = (type) => {
    const colorMap = {
      rag: "bg-blue-100 text-blue-800",
      slm: "bg-purple-100 text-purple-800",
      private_llm: "bg-amber-100 text-amber-800",
    };
    return colorMap[type] || "bg-slate-100 text-slate-800";
  };

  const getRoleColor = (role) => {
    const colorMap = {
      admin: "bg-red-100 text-red-800",
      user: "bg-blue-100 text-blue-800",
      viewer: "bg-slate-100 text-slate-800",
    };
    return colorMap[role] || "bg-slate-100 text-slate-800";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userServiceAccessApi.grant(formData);
      fetchData();
      resetForm();
    } catch (error) {
      console.error("Error granting access:", error);
    }
    setLoading(false);
  };

  // Keyed on the triple, not record.id. The endpoint keys on it — and the
  // previous signature, revoke(id), is how a hard DELETE sat here unnoticed
  // behind a confirm that read like a reversible action.
  const handleRevoke = async (record) => {
    const email = getUserEmail(getField(record, "user_id", "userId")) || "";
    const service = getField(record, "service_type", "serviceType") || "";
    const facility = getFacilityName(
      getField(record, "facility_id", "facilityId"),
    );
    if (
      !window.confirm(
        `Revoke ${service} access to ${facility} for ${email}?\n\n` +
          `They are signed out and lose access immediately. The record is kept, ` +
          `so you can restore it from this screen.`,
      )
    )
      return;
    setLoading(true);
    try {
      await userServiceAccessApi.revoke({
        user_id: getField(record, "user_id", "userId"),
        facility_id: getField(record, "facility_id", "facilityId"),
        service_type: getField(record, "service_type", "serviceType"),
      });
      fetchData();
    } catch (error) {
      console.error("Error revoking access:", error);
    }
    setLoading(false);
  };

  // Re-granting the same triple un-revokes the existing row (ON CONFLICT DO
  // UPDATE … is_active = true, revoked_at = NULL) rather than adding a second.
  const handleRestore = async (record) => {
    setLoading(true);
    try {
      await userServiceAccessApi.grant({
        user_id: getField(record, "user_id", "userId"),
        facility_id: getField(record, "facility_id", "facilityId"),
        service_type: getField(record, "service_type", "serviceType"),
        access_role: getField(record, "access_role", "accessRole") || "user",
      });
      fetchData();
    } catch (error) {
      console.error("Error restoring access:", error);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      user_id: "",
      facility_id: "",
      service_type: "rag",
      access_role: "user",
    });
    setShowForm(false);
  };

  const filteredRecords = accessRecords.filter((record) => {
    const userId = getField(record, "user_id", "userId") || "";
    const facilityId = getField(record, "facility_id", "facilityId") || "";
    const userName = getUserName(userId);
    const facilityName = getFacilityName(facilityId);

    return (
      userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      facilityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getUserEmail(userId).toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          onRefresh={fetchData}
          placeholder="Search access records..."
        />
        <PrimaryButton onClick={() => setShowForm(!showForm)} icon={Plus}>
          Grant Access
        </PrimaryButton>
      </div>

      <FormModal
        title="Grant Service Access"
        isOpen={showForm}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                User *
              </label>
              <select
                required
                value={formData.user_id}
                onChange={(e) =>
                  setFormData({ ...formData, user_id: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select user...</option>
                {users.map((user) => {
                  const uid = user.id || user.ID;
                  const first = user.first_name || user.firstName || "";
                  const last = user.last_name || user.lastName || "";
                  const email = user.email || user.Email || "";
                  return (
                    <option key={uid} value={uid}>
                      {first} {last} ({email})
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Facility *
              </label>
              <select
                required
                value={formData.facility_id}
                onChange={(e) =>
                  setFormData({ ...formData, facility_id: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select facility...</option>
                {facilities.map((f) => {
                  const fid = f.facility_id || f.facilityId || f.id;
                  const fname =
                    f.facility_name || f.facilityName || f.name || fid;
                  return (
                    <option key={fid} value={fid}>
                      {fname}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Service Type *
              </label>
              <select
                required
                value={formData.service_type}
                onChange={(e) =>
                  setFormData({ ...formData, service_type: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {SERVICE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Access Role *
              </label>
              <select
                required
                value={formData.access_role}
                onChange={(e) =>
                  setFormData({ ...formData, access_role: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {SERVICE_ACCESS_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={resetForm}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading} icon={Save}>
              {loading ? "Saving..." : "Grant Access"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Facility
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Service
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Granted
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredRecords.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  No access records found. Grant access to allow users to
                  interact with facility services.
                </td>
              </tr>
            ) : (
              filteredRecords.map((record) => {
                const userId = getField(record, "user_id", "userId") || "";
                const facilityId =
                  getField(record, "facility_id", "facilityId") || "";
                const serviceType =
                  getField(record, "service_type", "serviceType") || "";
                const accessRole =
                  getField(record, "access_role", "accessRole") || "";
                const grantedAt = getField(record, "granted_at", "grantedAt");
                const grantedBy =
                  getField(record, "granted_by", "grantedBy") || "";

                // `state` is computed server-side from is_active /
                // revoked_at / rejected_at so this screen and the API cannot
                // disagree about what a row means.
                const state =
                  record.state ??
                  (record.is_active === false ? "revoked" : "active");
                const isActive = state === "active";

                return (
                  <tr
                    key={record.id}
                    className={`hover:bg-slate-50 ${isActive ? "" : "bg-slate-50/60"}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div
                        className={`font-medium ${isActive ? "text-slate-900" : "text-slate-500"}`}
                      >
                        {getUserName(userId)}
                      </div>
                      <div className="text-xs text-slate-400">
                        {getUserEmail(userId)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {getFacilityName(facilityId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getServiceColor(serviceType)}`}
                      >
                        {getServiceLabel(serviceType)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(accessRole)}`}
                      >
                        {accessRole}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      <div>
                        {grantedAt
                          ? new Date(grantedAt).toLocaleDateString()
                          : "-"}
                      </div>
                      {grantedBy && (
                        <div className="text-xs text-slate-400">
                          by {grantedBy}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          state === "active"
                            ? "bg-green-100 text-green-700"
                            : state === "revoked"
                              ? "bg-amber-100 text-amber-700"
                              : state === "rejected"
                                ? "bg-red-100 text-red-700"
                                : "bg-slate-100 text-slate-600"
                        }`}
                        title={
                          state === "revoked"
                            ? `Revoked${record.revoked_at ? " " + new Date(record.revoked_at).toLocaleDateString() : ""} — the record is kept and can be restored`
                            : state === "pending"
                              ? "Requested but never approved"
                              : ""
                        }
                      >
                        {state}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {isActive ? (
                        <button
                          onClick={() => handleRevoke(record)}
                          className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remove access. Reversible — the record is kept."
                        >
                          Revoke
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRestore(record)}
                          className="px-2 py-1 text-xs text-green-700 hover:bg-green-50 rounded transition-colors"
                          title="Give this access back at the same role"
                        >
                          Restore
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserServiceAccessManagement;
